import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { X, BookOpen, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const ExitIntentPopup = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if user has already seen exit popup this session
    const hasSeenExitPopup = sessionStorage.getItem("exit_popup_shown");
    if (hasSeenExitPopup) return;

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves from top of page (likely closing tab)
      if (e.clientY <= 0 && !isOpen) {
        // Don't stack over an open modal (QuoteBuilder, dialogs)
        if (document.querySelector('[data-testid="quote-builder"], [role="dialog"]')) return;
        setIsOpen(true);
        sessionStorage.setItem("exit_popup_shown", "true");
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", duration: 0.5 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-background rounded-3xl shadow-2xl overflow-hidden"
            data-testid="exit-intent-popup"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors z-10"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="p-8 text-center">
              {/* Icon */}
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <BookOpen className="h-10 w-10 text-primary" strokeWidth={1.5} />
              </div>

              <h3 className="text-2xl font-bold mb-3">
                Before You Go
              </h3>
              
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                Every artwork tells a story of 65,000+ years of continuous culture. 
                Discover the piece that speaks to you.
              </p>

              <div className="space-y-3">
                <Link to="/the-collection" onClick={handleClose}>
                  <Button className="w-full py-6 rounded-xl text-base gap-2">
                    Explore the Collection
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
                
                <button
                  onClick={handleClose}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  No thanks, I'll continue browsing
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
